import { useMutation, useQueryClient } from '@tanstack/react-query'

import api from '@/lib/api'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'
import { extractApiErrorMessage, type DownloadFileResult, toDownloadFileResult } from './download-utils'

export interface GerarXmlCorrigidoRequest {
  file: File
  upgradeLegacyLayout?: boolean
  tpAmb?: string
  tbAmb?: string
  responseFormat?: 'json' | 'zip'
  idPrevidenciario: string
}

type GerarXmlCorrigidoResponse = DownloadFileResult | Record<string, unknown>

export function useGerarXmlCorrigido() {
  const queryClient = useQueryClient()
  const token = useAccessToken()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async ({
      file,
      upgradeLegacyLayout,
      tpAmb,
      tbAmb,
      responseFormat,
      idPrevidenciario
    }: GerarXmlCorrigidoRequest): Promise<GerarXmlCorrigidoResponse> => {
      if (!file) {
        throw new Error('Arquivo é obrigatório')
      }

      if (!idPrevidenciario?.trim()) {
        throw new Error('ID previdenciário é obrigatório')
      }

      if (!user || !token) {
        throw new Error('Usuário não autenticado')
      }

      const formData = new FormData()
      formData.append('file', file)

      if (typeof upgradeLegacyLayout === 'boolean') {
        formData.append('upgrade_legacy_layout', String(upgradeLegacyLayout))
      }
      if (tpAmb) formData.append('tpAmb', tpAmb)
      if (tbAmb) formData.append('tbAmb', tbAmb)
      const resolvedResponseFormat = responseFormat ?? 'zip'
      formData.append('response_format', resolvedResponseFormat)

      try {
        if (resolvedResponseFormat === 'zip') {
          const response = await api.post<Blob>(
            `/previdenciario/gerar/${idPrevidenciario}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              },
              responseType: 'blob'
            }
          )

          if (!response.data) {
            throw new Error('Erro ao gerar XML corrigido')
          }

          return toDownloadFileResult(response, 'xml-corrigidos.zip')
        }

        const response = await api.post<Record<string, unknown>>(
          `/previdenciario/gerar/${idPrevidenciario}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        )

        if (!response.data) {
          throw new Error('Erro ao gerar análise previdenciária')
        }

        return response.data
      } catch (error) {
        throw new Error(await extractApiErrorMessage(error, 'Erro ao gerar XML corrigido'))
      }
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ['previdenciario-detalhes', request.idPrevidenciario] })
      queryClient.invalidateQueries({ queryKey: ['esocial-retornos', request.idPrevidenciario] })
    }
  })
}
